import URI from "urijs";
import axios from "axios";
import Url from "~/modules/url";
import Log from "~/modules/log";
import * as types from "../mutation-types";

// Disabling shadow as state object is shadowed, but this is the Vue best practice.
/* eslint no-shadow: 0 */

const state = {
  templates: {},
  configNode: 0,
  filters: [],
  results: [],
  sorts: [],
  pageIndex: 0,
  resultCount: 0,
  itemsPerPage: 24,
  layout: "grid",
  columnCount: 2,
  index: 0,
  seed: null,
  activeResultTemplate: "Default",
  loading: false,
  hydrated: false,
  infinite: false
};

const getters = {
  appliedFilters: state => () => state.filters.filter(
    ( element ) => element.value,
  )
};

const actions = {
  initialise ( context ) {
    const uri = new URI( window.document.location.href );
    const parameters = uri.search( true );

    // Iterate through query string parameters
    Object.entries( parameters ).forEach( ( parameter ) => {
      const key = parameter[0];
      const value = parameter[1];

      // Look for matching filters and apply them
      const filter = context.state.filters.find(
        ( filter ) => filter.alias === key,
      );
      if ( filter ) {
        context.commit( types.SEARCH_APPLY_FILTER, {
          group: filter.group,
          filter: key,
          value
        } );
      }

      // Look for pagination parameter and apply
      if ( key === "page" ) {
        context.commit( types.SEARCH_PAGINATE, {
          pageIndex: value - 1
        } );
      }

      // Look for sort parameter and apply
      if ( key === "sort" ) {
        context.commit( types.SEARCH_APPLY_SORT, {
          sort: value
        } );
      }
    } );

    if ( !context.state.hydrated && context.state.nodeId !== 0 ) {
      Log.debug( "Running an initial search", null, "search" );

      // Perform the initial search
      context.dispatch( "search" );
    }
  },
  applyFilter ( context, payload ) {
    const newValue = payload.value;
    const {
      value: currentValue
    } = context.state.filters.find(
      ( filter ) => filter.alias === payload.filter,
    );

    if ( currentValue !== newValue ) {
      Log.debug( "applying filter", {
        currentValue,
        newValue
      }, "search" );

      // Apply the filter to the store
      context.commit( types.SEARCH_APPLY_FILTER, payload );

      // Update the browser URL with the new query string
      Url.updateQueryString( payload.filter, payload.value );

      // Reset pagination back to first page
      context.commit( types.SEARCH_PAGINATE, {
        pageIndex: 0
      } );
      Url.updateQueryString( "page", 1 );

      // Perform the search
      context.dispatch( "search" );
    } else {
      Log.warning( "Not applying filter as value is identical", {
        payload
      }, "search" );
    }
  },
  applySort ( context, payload ) {
    const newValue = payload.pageIndex;
    const currentValue = context.state.pageIndex;

    if ( newValue !== currentValue ) {
      // Apply the sort to the store
      context.commit( types.SEARCH_APPLY_SORT, payload );

      // Update the browser URL with the new query string
      Url.updateQueryString( "sort", payload.sort );

      // Perform the search
      context.dispatch( "search" );
    }
  },
  search ( context, payload ) {
    Log.debug( "Calling search AJAX!", null, "search" );

    // Check if we are infinite scrolling
    let infinite = false;
    if ( payload && payload.infinite != null ) {
      ( {
        infinite
      } = payload );
    }

    context.commit( types.SEARCH_UPDATE_INDEX, {
      index: context.state.index + 1
    } );

    const baseUrl = "/umbraco/Surface/SearchSurface/Search";
    const composedUrl = URI( baseUrl ).query( {
      nodeId: context.state.configNode,
      filters: window.location.search,
      page: context.state.pageIndex,
      index: context.state.index,
      seed: context.state.seed,
      infinite: false
    } );

    // Set the "is loading" flag
    context.commit( types.SEARCH_SET_IS_LOADING, true );

    // Use Axios to retrieve the results
    axios.get( composedUrl )
      .then( ( response ) => {
        const {
          results
        } = response.data;
        const {
          filters
        } = response.data;
        const {
          resultCount
        } = response.data;
        const {
          index
        } = response.data;
        const {
          activeResultTemplate
        } = response.data;

        Log.debug( "AJAX request returned these results", response.data, "search" );
        if ( index === context.state.index ) {
          Log.debug( "AJAX index looks good, commiting changes", index, "search" );

          // Put the results in Vuex store
          context.commit( types.SEARCH_UPDATE_RESULTS, {
            results,
            resultCount,
            infinite
          } );

          context.commit( types.SEARCH_UPDATE_FILTERS, {
            filters
          } );

          if ( activeResultTemplate !== context.state.activeResultTemplate ) {
            context.commit( types.SEARCH_UPDATE_ACTIVE_RESULT_TEMPLATE, {
              activeResultTemplate
            } );
          }
        } else {
          Log.debug( "AJAX index is out of date, throwing away changes", index, "search" );
        }

        // Unset the "is loading" flag
        context.commit( types.SEARCH_SET_IS_LOADING, false );
      } )
      .catch( ( error ) => {
        Log.error( "Error performing AJAX request", error, "search" );

        // Unset the "is loading" flag
        context.commit( types.SEARCH_SET_IS_LOADING, false );
      } );
  },
  paginate ( context, payload ) {
    const newValue = payload.pageIndex;
    const currentValue = context.state.pageIndex;

    if ( newValue !== currentValue ) {
      // Apply the pagination to the store
      context.commit( types.SEARCH_PAGINATE, payload );

      // Update the browser URL with the new query string
      Url.updateQueryString( "page", payload.pageIndex + 1 );

      let searchPayload = null;
      if ( payload.infinite ) {
        searchPayload = {
          infinite: true
        };
      }

      // Perform the search
      context.dispatch( "search", searchPayload );
    }
  },
  updateLayout ( context, payload ) {
    // Update the stored layout
    context.commit( types.SEARCH_UPDATE_LAYOUT, payload );
  }
};

const mutations = {
  [types.SEARCH_APPLY_FILTER] ( state, payload ) {
    const [filterGroup] = state.filters.filter(
      ( element ) => element.name === payload.group,
    );

    if ( filterGroup != null ) {
      const [filter] = filterGroup.filters.filter(
        ( element ) => element.alias === payload.filter,
      );

      if ( filter != null ) {
        filter.value = payload.value;
      }
    }
  },
  [types.SEARCH_APPLY_SORT] ( state, payload ) {
    state.sorts.forEach( sort => {
      if ( sort.alias === payload.sort ) {
        sort.active = true;
      } else {
        sort.active = false;
      }
    } );
  },
  [types.SEARCH_UPDATE_FILTERS] ( state, payload ) {
    state.filters = payload.filters;
  },
  [types.SEARCH_UPDATE_RESULTS] ( state, payload ) {
    if ( payload.infinite ) {
      state.results.push( ...payload.results );
    } else {
      state.results = payload.results;
    }

    state.resultCount = payload.resultCount;
  },
  [types.SEARCH_PAGINATE] ( state, payload ) {
    state.pageIndex = payload.pageIndex;
  },
  [types.SEARCH_UPDATE_LAYOUT] ( state, payload ) {
    state.layout = payload.layout;
  },
  [types.SEARCH_UPDATE_INDEX] ( state, payload ) {
    state.index = payload.index;
  },
  [types.SEARCH_UPDATE_ACTIVE_RESULT_TEMPLATE] ( state, payload ) {
    state.activeResultTemplate = payload.activeResultTemplate;
  },
  [types.SEARCH_SET_IS_LOADING] ( state, payload ) {
    state.loading = payload;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
